import React, { useCallback, Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { themeGet } from 'styled-system';
import { useDataLayer } from 'hooks/useDataLayer';
import { useModal } from 'lib/hooks';
import { Text, Icon, NakedButton } from 'roo-ui/components';
import Modal from 'components/Modal';
import MapWithMarker from 'components/maps/MapWithMarker';
import formatPropertyAddress from 'lib/formatters/formatPropertyAddress';

const getFullAddress = (address) =>
  formatPropertyAddress({
    streetAddressLines: address.streetAddress,
    suburb: address.suburb,
    state: address.state,
    country: address.country,
  });

const Address = ({ address }) => (
  <Fragment>
    <Icon name="place" size={16} color="greys.steel" mr={2} />
    <Text fontSize="base" color="greys.steel" bg="white" mr={2} lineHeight="tight">
      {getFullAddress(address)}
    </Text>
  </Fragment>
);

Address.propTypes = {
  address: PropTypes.shape({
    streetAddress: PropTypes.arrayOf(PropTypes.string).isRequired,
    suburb: PropTypes.string.isRequired,
    state: PropTypes.string,
    postcode: PropTypes.string.isRequired,
    country: PropTypes.string.isRequired,
  }).isRequired,
};

Address.displayName = 'PropertyAddress';

const HoverButtonText = styled(NakedButton)`
  font-size: base;
  color: ${themeGet('colors.brand.primary')};
  cursor: pointer;
  &:hover {
    color: maroon;
  }
`;

const ViewOnMapWithAddress = ({ latitude, longitude, locationName, address }) => {
  const { openModal, modalProps } = useModal();
  const { emitInteractionEvent } = useDataLayer();

  const handleViewOnMapOnClick = useCallback(() => {
    openModal();
    emitInteractionEvent({ type: 'Map View', value: 'View On Map Link Selected' });
  }, [emitInteractionEvent, openModal]);

  return (
    <Fragment>
      <HoverButtonText onClick={handleViewOnMapOnClick} data-testid="view-on-map-and-address">
        <Address address={address} mt={4} />
        <Text fontSize="base" textDecoration="underline">
          View on map
        </Text>
      </HoverButtonText>
      <Modal {...modalProps} fullScreen padding="0" title={locationName}>
        <MapWithMarker name="map-with-marker" data-testid="map-with-marker" latitude={latitude} longitude={longitude} />
      </Modal>
    </Fragment>
  );
};

ViewOnMapWithAddress.propTypes = {
  latitude: PropTypes.number.isRequired,
  longitude: PropTypes.number.isRequired,
  locationName: PropTypes.string.isRequired,
  address: PropTypes.object.isRequired,
};

export default ViewOnMapWithAddress;
